import useQuizCountdown from "../../hooks/useQuizCountdown";
import QuizQuestionShimmer from "./QuizQuestionShimmer";
import { QUIZ_ROUTES } from "./quizRoutes";
import React, { useEffect, useMemo, useRef, useState } from "react";
import DOMPurify from "dompurify";
import Swal from "sweetalert2";
import { useLocation, useNavigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import HomeDashboardLayout from "../HomeDashboardLayout";
import QuestionPallete from "../questionBank/QuestionPallete";
import HeaderProfileSection from "../HeaderProfileSection";
import PauseTestQuizModal from "../../components/PauseTestQuizModal";
import QuizSubmitModal from "../../components/QuizSubmitModal";
import ReportModal from "../../components/ReportModal";
import StickyNotesPopup from "../../components/StickyNotesPopup";
import ViewModal from "../../components/ViewModal";
import {
  useQuestionBankQuestionFeedback,
  useQuestionNotes,
} from "../../hooks/useQuestionBankQuestionFeedback";
import { useQuestionBankQuestionReference } from "../../hooks/useQuestionBankPractice";
import {
  useQuizPracticeMutations,
  useQuizPracticePalette,
  useQuizPracticeQuestions,
} from "../../hooks/useQuizPractice";
import {
  toggleQuestionBookmark,
  toggleQuestionLike,
} from "../../services/questionBankQuestionService";
import "../../dashboard/Dashboard.css";
import "../HomeDashboard.css";
import "../questionBank/QbankDetail.css";
import "../../questionBank/QuestionBankPractice.css";
import { buildQuizBackTarget, buildQuizFlowState, buildStartQuizState, getStoredQuizFlowState, persistQuizFlowState, navigateQuizBack } from "./quizFlowState";
import { installQuizHistoryGuard } from './quizHistoryGuard';

function formatDuration(totalSeconds) {
  const seconds = Math.max(0, Number(totalSeconds || 0));
  const hrs = String(Math.floor(seconds / 3600)).padStart(2, "0");
  const mins = String(Math.floor((seconds % 3600) / 60)).padStart(2, "0");
  const secs = String(seconds % 60).padStart(2, "0");
  return `${hrs}:${mins}:${secs}`;
}

function stripHtml(value) {
  return String(value || "").replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
}

function getQuestionSequence(question = {}, fallback = 0) {
  return Number(
    question?.serialNo ||
      question?.question_index ||
      question?.question_number ||
      question?.count ||
      fallback ||
      0
  );
}

function toOptionLabel(index) {
  return String.fromCharCode(65 + index);
}

function resolveSelectedOptionId(question, selectedOptionIdOverride = 0) {
  return Number(
    selectedOptionIdOverride ||
      question?.selectedOptionId ||
      question?.options?.find((option) => option?.isSelected)?.id ||
      0
  );
}

function syncOptionSelection(options = [], selectedOptionId = 0) {
  const normalizedSelectedOptionId = Number(selectedOptionId || 0);

  return options.map((option) => ({
    ...option,
    isSelected: Number(option?.id) === normalizedSelectedOptionId,
  }));
}

function deriveSelectedOptionId(question = {}) {
  return Number(
    question?.selectedOptionId ||
      question?.options?.find((option) => option?.isSelected)?.id ||
      question?.raw?.selected_option_id ||
      question?.raw?.question_option?.find((option) => Number(option?.user_selected_option || 0) === 1)
        ?.option_id ||
      0
  );
}

function toOptionTone(
  question,
  option,
  optionId,
  selectedOptionIdOverride = 0,
  revealCorrectness = true
) {
  const isSelected =
    resolveSelectedOptionId(question, selectedOptionIdOverride) === Number(optionId);
  if (question?.isAnswered && !revealCorrectness) {
    return isSelected ? "selected" : "neutral";
  }
  if (question?.isAnswered && option?.isCorrect) {
    return "correct";
  }
  if (question?.isAnswered && isSelected && !option?.isCorrect) {
    return "wrong";
  }
  if (isSelected) {
    return "selected";
  }
  return "neutral";
}

function mergeQuestion(previousQuestion, nextQuestion) {
  if (!previousQuestion) {
    return nextQuestion;
  }

  const nextSelectedOptionId = deriveSelectedOptionId(nextQuestion);
  const previousSelectedOptionId = deriveSelectedOptionId(previousQuestion);
  const resolvedSelectedOptionId = nextSelectedOptionId || previousSelectedOptionId || 0;
  const raw = {
    ...(previousQuestion?.raw || {}),
    ...(nextQuestion?.raw || {}),
  };
  const nextIsAnswered = Boolean(nextQuestion?.isAnswered);
  const previousIsAnswered = Boolean(previousQuestion?.isAnswered);
  const resolvedIsAnswered =
    nextIsAnswered || previousIsAnswered || Boolean(resolvedSelectedOptionId);
  const resolvedIsGuess =
    nextQuestion?.isGuess !== undefined ? Boolean(nextQuestion.isGuess) : Boolean(previousQuestion?.isGuess);
  const nextOptions = nextQuestion?.options?.length ? nextQuestion.options : previousQuestion?.options || [];

  return {
    ...previousQuestion,
    ...nextQuestion,
    selectedOptionId: resolvedSelectedOptionId || null,
    isAnswered: resolvedIsAnswered,
    isGuess: resolvedIsGuess,
    options: resolvedSelectedOptionId
      ? syncOptionSelection(nextOptions, resolvedSelectedOptionId)
      : nextOptions,
    raw: {
      ...raw,
      selected_option_id: resolvedSelectedOptionId || raw?.selected_option_id || 0,
      is_question_answered:
        resolvedIsAnswered ? 1 : raw?.is_question_answered,
    },
  };
}

function isAffirmativeFlag(value) {
  if (value === true) {
    return true;
  }

  const normalized = String(value ?? "").trim().toUpperCase();
  return normalized === "1" || normalized === "Y" || normalized === "YES" || normalized === "TRUE";
}

function QuizDetailShimmer() {
  return (
    <div role="status" aria-label="Loading quiz questions" className="categories-page qbank-detail-page  qbank-practiceShimmer " style={{ background: "#fff" }}>
      <div className="categories-page-header">
        <div>
          <span className="dashboard-skeleton dashboard-skeleton--icon qbank-practiceShimmer__back" />
        </div>
        <div className="qbank-practiceShimmer__headerTitleWrap">
          <span className="dashboard-skeleton dashboard-skeleton--title qbank-practiceShimmer__headerTitle" />
        </div>
        <div>
          <span className="dashboard-skeleton dashboard-skeleton--icon qbank-practiceShimmer__profile" />
        </div>
        
      </div>

      <div className="qb-Detail-content">
        <div className="d-flex qbank-detail-main">
          <section className="qbank-LeftContent" aria-hidden="true">
            <div className="qbank-card qbank-card--question">
              <div className="qbank-questionTop">
                <span className="dashboard-skeleton dashboard-skeleton--title qbank-practiceShimmer__progress" />
                <span className="dashboard-skeleton dashboard-skeleton--button qbank-practiceShimmer__timer" />
                <div className="qbank-toolbar">
                  <span className="dashboard-skeleton dashboard-skeleton--icon qbank-practiceShimmer__tool" />
                  <span className="dashboard-skeleton dashboard-skeleton--icon qbank-practiceShimmer__tool" />
                  <span className="dashboard-skeleton dashboard-skeleton--icon qbank-practiceShimmer__tool" />
                </div>
              </div>

              <div className="data-question-scroll">
                <div className="qbank-questionBlock">
                  <span className="dashboard-skeleton dashboard-skeleton--line qbank-practiceShimmer__questionLine" />
                  <span className="dashboard-skeleton dashboard-skeleton--line qbank-practiceShimmer__questionLine qbank-practiceShimmer__questionLine--medium" />
                  <span className="dashboard-skeleton dashboard-skeleton--line qbank-practiceShimmer__questionLine qbank-practiceShimmer__questionLine--short" />
                </div>

                <div className="qbank-options">
                  {Array.from({ length: 4 }).map((_, index) => (
                    <div className="qbank-option qbank-practiceShimmer__option" key={index}>
                      <span className="dashboard-skeleton dashboard-skeleton--icon qbank-practiceShimmer__optionBadge" />
                      <div className="qbank-option__copy qbank-practiceShimmer__optionCopy">
                        <span className="dashboard-skeleton dashboard-skeleton--line qbank-practiceShimmer__optionLine" />
                        <span className="dashboard-skeleton dashboard-skeleton--line qbank-practiceShimmer__optionLine qbank-practiceShimmer__optionLine--short" />
                      </div>
                    </div>
                  ))}
                </div>

                <div className="qbank-guess qbank-practiceShimmer__guess">
                  <span className="dashboard-skeleton dashboard-skeleton--icon qbank-practiceShimmer__guessCheck" />
                  <span className="dashboard-skeleton dashboard-skeleton--line qbank-practiceShimmer__guessLabel" />
                </div>
              </div>

              <div className="footer-nav-fixed footer-nav-fixed--desktop qbank-practiceShimmer__footer">
                <div className="qbank-navRow">
                  <span className="dashboard-skeleton dashboard-skeleton--button qbank-practiceShimmer__navBtn" />
                  <span className="dashboard-skeleton dashboard-skeleton--icon qbank-practiceShimmer__navMenu" />
                  <span className="dashboard-skeleton dashboard-skeleton--button qbank-practiceShimmer__navBtn" />
                </div>
              </div>
            </div>
          </section>

          <aside className="qbank-rightContent" aria-hidden="true">
            {Array.from({ length: 3 }).map((_, index) => (
              <section className="qbank-sideCard qbank-practiceShimmer__sideCard" key={index}>
                <div className="qbank-sideCard__head">
                  <span className="dashboard-skeleton dashboard-skeleton--title qbank-practiceShimmer__sideTitle" />
                  <span className="dashboard-skeleton dashboard-skeleton--icon qbank-practiceShimmer__sideToggle" />
                </div>
                <div className="qbank-sideCard__body">
                  <span className="dashboard-skeleton dashboard-skeleton--line qbank-practiceShimmer__sideLine" />
                  <span className="dashboard-skeleton dashboard-skeleton--line qbank-practiceShimmer__sideLine qbank-practiceShimmer__sideLine--short" />
                </div>
              </section>
            ))}
          </aside>
        </div>

        <div className="Qbank-sidebar qbank-detail-sidebar qbank-detail-sidebar--desktop" aria-hidden="true">
          <div className="palette-container qbank-practiceShimmer__palette">
            <div className="qbank-paletteTabs">
              <span className="dashboard-skeleton dashboard-skeleton--button qbank-practiceShimmer__paletteTab" />
              <span className="dashboard-skeleton dashboard-skeleton--button qbank-practiceShimmer__paletteTab" />
            </div>

            <div className="palette-summary">
              {Array.from({ length: 4 }).map((_, index) => (
                <div className="summary-card qbank-practiceShimmer__summaryCard" key={index}>
                  <span className="dashboard-skeleton dashboard-skeleton--button qbank-practiceShimmer__summaryCount" />
                  <span className="dashboard-skeleton dashboard-skeleton--line qbank-practiceShimmer__summaryLabel" />
                </div>
              ))}
            </div>

            <div className="question-grid">
              {Array.from({ length: 15 }).map((_, index) => (
                <span className="dashboard-skeleton dashboard-skeleton--button qbank-practiceShimmer__paletteItem" key={index} />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const QuizDetail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const persistedFlowState = useMemo(() => getStoredQuizFlowState(), []);
  const flowState = useMemo(
    () => buildQuizFlowState(location.state, persistedFlowState),
    [location.state, persistedFlowState]
  );
  const quizData = flowState?.quizData || {};
  const quizId = Number(quizData?.quizId || quizData?.quiz_id || quizData?.id || 0);
  const statesId = Number(flowState?.state_id || quizData?.statesId || quizData?.states_id || 0);
  const categoryId = Number(
    flowState?.category_id || quizData?.categoryId || quizData?.category_id || 0
  );
  const selectedLang = (() => {
    const resolvedValue = String(
      flowState?.selected_lang ||
        flowState?.selectedMode?.langShortName ||
        quizData?.selected_lang ||
        "EN"
    )
      .trim()
      .toUpperCase();

    return ["EN", "HI", "BOTH"].includes(resolvedValue) ? resolvedValue : "EN";
  })();
  const assesmentBatchId = Number(flowState?.assesment_batch_id || 0);
  const shouldAutoResume = Boolean(flowState?.resume_on_open);
  const timerQuestionIdRef = useRef(0);
  const guessFlagsRef = useRef({});
  const fetchedPagesRef = useRef(new Set());
  const fetchingNextPageRef = useRef(false);
  const pendingNextQuestionIndexRef = useRef(null);
  const pendingQuestionSequenceRef = useRef(0);
  const pendingNavigationRef = useRef(null);
  const allowExitRef = useRef(false);
  const autoResumeTriggeredRef = useRef(false);
  const autoSubmitTriggeredRef = useRef(false);
  const latestCurrentQuestionIdRef = useRef(0);
  const latestQuestionTimersRef = useRef({});
  const latestQuestionBaseParamsRef = useRef({});

  const [currentPage, setCurrentPage] = useState(1);
  const [currentQuestionId, setCurrentQuestionId] = useState(0);
  const [questionsById, setQuestionsById] = useState({});
  const [questionOrder, setQuestionOrder] = useState([]);
  const [questionTimers, setQuestionTimers] = useState({});
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [guessFlags, setGuessFlags] = useState({});
  const [isPaletteDrawerOpen, setIsPaletteDrawerOpen] = useState(false);
  const [isPaletteDrawerEnabled, setIsPaletteDrawerEnabled] = useState(
    () => typeof window !== "undefined" && window.innerWidth < 1199.98
  );
  const [isExplanationOpen, setIsExplanationOpen] = useState(true);
  const [isPYPOpen, setIsPYPOpen] = useState(true);
  const [isNotesOpen, setIsNotesOpen] = useState(true);
  const [activeModal, setActiveModal] = useState(null);
  const [sidebarVisibleCount, setSidebarVisibleCount] = useState(50);
  const [isFetchingNextPage, setIsFetchingNextPage] = useState(false);
  const [isQuestionTransitioning, setIsQuestionTransitioning] = useState(false);
  const [screenError, setScreenError] = useState("");
  const [isSubmitModalOpen, setIsSubmitModalOpen] = useState(false);
  const [remainingSeconds, setRemainingSeconds] = useState(null);
  const [isQuizPaused, setIsQuizPaused] = useState(false);
  const [isPauseModalOpen, setIsPauseModalOpen] = useState(false);

  const startQuizState = useMemo(() => buildStartQuizState(flowState), [flowState]);
  const detailFlowState = useMemo(
    () =>
      buildQuizFlowState({
        ...flowState,
        quizBackTarget: buildQuizBackTarget(QUIZ_ROUTES.start, startQuizState),
      }),
    [flowState, startQuizState]
  );

  const questionBaseParams = useMemo(
    () => ({
      ...(statesId > 0 ? { states_id: statesId } : {}),
      category_id: categoryId,
      quiz_id: quizId,
      assesment_batch_id: assesmentBatchId,
      selected_lang: selectedLang,
    }),
    [assesmentBatchId, categoryId, quizId, selectedLang, statesId]
  );

  const questionsQuery = useQuizPracticeQuestions(
    {
      ...questionBaseParams,
      page: currentPage,
    },
    {
      enabled: quizId > 0 && categoryId > 0 && assesmentBatchId > 0,
    }
  );
  const paletteSummaryQuery = useQuizPracticePalette(questionBaseParams, {
    enabled: quizId > 0 && categoryId > 0 && assesmentBatchId > 0,
  });
  const { saveAnswer, saveTime, saveGuess, submitQuiz, saveAssessmentSubmitStatus, togglePauseQuiz } =
    useQuizPracticeMutations();
  const activeQuestionsData = questionsQuery.data;
  const quizMode = String(
    activeQuestionsData?.quizMode ||
      flowState?.selectedMode?.dataKey ||
      flowState?.selectedMode?.id ||
      quizData?.pendingQuizInfo?.quiz_mode ||
      "P"
  )
    .trim()
    .toUpperCase();
  const isEvaluationMode = quizMode === "E";
  useEffect(() => {
    persistQuizFlowState(detailFlowState);
  }, [detailFlowState]);

  useEffect(() => {
    guessFlagsRef.current = guessFlags;
  }, [guessFlags]);
  const currentQuestionAnswered = Boolean(questionsById[currentQuestionId]?.isAnswered);
  const notesQuery = useQuestionNotes(Number(currentQuestionId || 0), {
    enabled: Boolean(currentQuestionId),
  });
  const referenceQuery = useQuestionBankQuestionReference(
    {
      ...questionBaseParams,
      question_id: Number(currentQuestionId || 0),
    },
    {
      enabled: Boolean(currentQuestionId) && currentQuestionAnswered && !isEvaluationMode,
    }
  );
  const { deleteNote } = useQuestionBankQuestionFeedback(questionBaseParams);
  const bookmarkMutation = useMutation({
    mutationFn: toggleQuestionBookmark,
  });
  const likeMutation = useMutation({
    mutationFn: toggleQuestionLike,
  });

  useEffect(() => {
    const handleResize = () => {
      const isMobile = window.innerWidth < 1199.98;
      setIsPaletteDrawerEnabled(isMobile);

      if (!isMobile) {
        setIsPaletteDrawerOpen(false);
      }
    };

    window.addEventListener("resize", handleResize);
    handleResize();

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (!isPaletteDrawerOpen) {
      return undefined;
    }

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [isPaletteDrawerOpen]);

  useEffect(() => {
    if (!quizId || !categoryId || !assesmentBatchId) {
      return undefined;
    }

    const removeHistoryGuard = installQuizHistoryGuard(() => setIsPauseModalOpen(true), () => isQuizPaused || allowExitRef.current);

    const handleDocumentNavigation = (event) => {
      if (isQuizPaused || togglePauseQuiz.isPending) {
        return;
      }

      if (
        event.defaultPrevented ||
        event.button !== 0 ||
        event.metaKey ||
        event.altKey ||
        event.ctrlKey ||
        event.shiftKey
      ) {
        return;
      }

      const anchor = event.target?.closest?.("a[href]");
      if (!anchor) {
        return;
      }

      const rawHref = anchor.getAttribute("href") || "";
      if (
        !rawHref ||
        rawHref.startsWith("#") ||
        anchor.target === "_blank" ||
        anchor.hasAttribute("download")
      ) {
        return;
      }

      let nextUrl;
      try {
        nextUrl = new URL(anchor.href, window.location.origin);
      } catch (_error) {
        return;
      }

      if (nextUrl.origin !== window.location.origin) {
        return;
      }

      const nextRoute = `${nextUrl.pathname}${nextUrl.search}${nextUrl.hash}`;
      const currentRoute = `${window.location.pathname}${window.location.search}${window.location.hash}`;
      if (nextRoute === currentRoute) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      pendingNavigationRef.current = nextRoute;
      setIsPauseModalOpen(true);
    };

    document.addEventListener("click", handleDocumentNavigation, true);

    return () => {
      removeHistoryGuard();
      document.removeEventListener("click", handleDocumentNavigation, true);
    };
  }, [assesmentBatchId, categoryId, isQuizPaused, quizId, togglePauseQuiz.isPending]);

  useEffect(() => {
    if (!activeQuestionsData?.questions?.length) {
      return;
    }
    const fetchedPage = Number(activeQuestionsData?.pagination?.currentPage || currentPage);
    if (questionsQuery.isPlaceholderData && fetchedPage !== Number(currentPage)) {
      return;
    }

    fetchedPagesRef.current.add(fetchedPage);

    setQuestionsById((current) => {
      const nextValue = { ...current };
      activeQuestionsData.questions.forEach((question) => {
        nextValue[question.id] = mergeQuestion(current[question.id], question);
      });
      return nextValue;
    });

    setQuestionOrder((current) => {
      const nextOrder = [...current];
      activeQuestionsData.questions.forEach((question) => {
        if (!nextOrder.includes(question.id)) {
          nextOrder.push(question.id);
        }
      });
      return nextOrder.sort((firstQuestionId, secondQuestionId) => {
        const firstQuestion =
          questionsById[firstQuestionId] ||
          activeQuestionsData.questions.find((question) => Number(question.id) === Number(firstQuestionId)) ||
          {};
        const secondQuestion =
          questionsById[secondQuestionId] ||
          activeQuestionsData.questions.find((question) => Number(question.id) === Number(secondQuestionId)) ||
          {};

        return getQuestionSequence(firstQuestion) - getQuestionSequence(secondQuestion);
      });
    });

    setQuestionTimers((current) => {
      const nextValue = { ...current };
      activeQuestionsData.questions.forEach((question) => {
        if (nextValue[question.id] === undefined) {
          nextValue[question.id] = Number(question.questionTime || 0);
        }
      });
      return nextValue;
    });

    setSelectedAnswers((current) => {
      const nextValue = { ...current };
      activeQuestionsData.questions.forEach((question) => {
        if (question.selectedOptionId && nextValue[question.id] === undefined) {
          nextValue[question.id] = question.selectedOptionId;
        }
      });
      return nextValue;
    });

    setGuessFlags((current) => {
      const nextValue = { ...current };
      activeQuestionsData.questions.forEach((question) => {
        if (nextValue[question.id] === undefined) {
          nextValue[question.id] = Boolean(question.isGuess);
        }
      });
      return nextValue;
    });

    if (!currentQuestionId) {
      setCurrentQuestionId(Number(activeQuestionsData.questions[0]?.id || 0));
    }
  }, [activeQuestionsData, currentPage, currentQuestionId, questionsQuery.isPlaceholderData]);

  useEffect(() => {
    const totalDuration = Number(activeQuestionsData?.durations || activeQuestionsData?.duration || 0);
    if (totalDuration > 0 && remainingSeconds === null) {
      setRemainingSeconds(totalDuration);
    }
  }, [activeQuestionsData?.duration, activeQuestionsData?.durations, remainingSeconds]);

  useEffect(() => {
    if (activeQuestionsData?.isPaused !== undefined) {
      setIsQuizPaused(Boolean(activeQuestionsData.isPaused));
    }
  }, [activeQuestionsData?.isPaused]);

  useEffect(() => {
    const resumeQuestionId = Number(questionsById[currentQuestionId]?.id || currentQuestionId || 0);

    if (
      !shouldAutoResume ||
      autoResumeTriggeredRef.current ||
      !Boolean(activeQuestionsData?.isPaused) ||
      !resumeQuestionId ||
      togglePauseQuiz.isPending
    ) {
      return;
    }

    autoResumeTriggeredRef.current = true;

    const resumeQuiz = async () => {
      try {
        await togglePauseQuiz.mutateAsync({
          states_id: statesId,
          category_id: categoryId,
          quiz_id: quizId,
          assesment_batch_id: assesmentBatchId,
          question_id: resumeQuestionId,
          selected_lang: selectedLang,
          duration_sec: Number(questionTimers[resumeQuestionId] || 0),
          is_paused: 0,
        });

        setIsQuizPaused(false);
        const nextFlowState = buildQuizFlowState({
          ...detailFlowState,
          resume_on_open: false,
        });
        persistQuizFlowState(nextFlowState);
      } catch (error) {
        autoResumeTriggeredRef.current = false;
        setScreenError(error?.message || "Unable to resume quiz.");
      }
    };

    resumeQuiz();
  }, [
    activeQuestionsData?.isPaused,
    assesmentBatchId,
    categoryId,
    currentQuestionId,
    detailFlowState,
    questionTimers,
    questionsById,
    quizId,
    isEvaluationMode,
    selectedLang,
    shouldAutoResume,
    statesId,
    togglePauseQuiz,
  ]);

  useQuizCountdown(remainingSeconds, isQuizPaused, setRemainingSeconds);

  useEffect(() => {
    const pendingQuestionIndex = pendingNextQuestionIndexRef.current;
    if (pendingQuestionIndex === null || pendingQuestionIndex === undefined) {
      return;
    }

    if (questionOrder.length <= pendingQuestionIndex) {
      return;
    }

    setCurrentQuestionId(questionOrder[pendingQuestionIndex]);
    pendingNextQuestionIndexRef.current = null;
    setIsQuestionTransitioning(false);
  }, [questionOrder]);

  useEffect(() => {
    const pendingSequence = Number(pendingQuestionSequenceRef.current || 0);
    if (!pendingSequence) {
      return;
    }

    const targetQuestion = Object.values(questionsById).find(
      (question) => getQuestionSequence(question) === pendingSequence
    );
    if (!targetQuestion?.id) {
      return;
    }

    setCurrentQuestionId(Number(targetQuestion.id));
    pendingQuestionSequenceRef.current = 0;
    setIsQuestionTransitioning(false);
  }, [questionsById]);

  const currentQuestion = questionsById[currentQuestionId] || null;
  const isQuestionLoading = isQuestionTransitioning || (!currentQuestion && (questionsQuery.isLoading || questionsQuery.isFetching));
  latestCurrentQuestionIdRef.current = Number(currentQuestionId || 0);
  latestQuestionTimersRef.current = questionTimers;
  latestQuestionBaseParamsRef.current = questionBaseParams;
  const paletteApiItems = paletteSummaryQuery.data?.items || [];
  const currentQuestionIndex = questionOrder.findIndex(
    (questionId) => Number(questionId) === Number(currentQuestionId)
  );
  const totalQuestionCount = Number(
    activeQuestionsData?.totalQuestions ||
      currentQuestion?.totalQuestions ||
      paletteSummaryQuery.data?.totalQuestions ||
      questionOrder.length ||
      0
  );

  useEffect(() => {
    if (!currentQuestionId || isQuizPaused || currentQuestionAnswered || isQuestionLoading) {
      return undefined;
    }

    timerQuestionIdRef.current = currentQuestionId;

    const intervalId = window.setInterval(() => {
      const activeQuestionId = Number(timerQuestionIdRef.current || 0);
      if (!activeQuestionId) {
        return;
      }

      setQuestionTimers((current) => ({
        ...current,
        [activeQuestionId]: Number(current[activeQuestionId] || 0) + 1,
      }));
    }, 1000);

    return () => window.clearInterval(intervalId);
  }, [currentQuestionAnswered, currentQuestionId, isQuizPaused, isQuestionLoading]);

  const syncQuestionPatch = (questionId, patch) => {
    setQuestionsById((current) => ({
      ...current,
      [questionId]: mergeQuestion(current[questionId], {
        ...current[questionId],
        ...patch,
      }),
    }));
  };

  const syncSelectedAnswerState = (questionId, selectedOptionId) => {
    const normalizedQuestionId = Number(questionId || 0);
    const normalizedSelectedOptionId = Number(selectedOptionId || 0);
    if (!normalizedQuestionId || !normalizedSelectedOptionId) {
      return;
    }

    setSelectedAnswers((current) => ({
      ...current,
      [normalizedQuestionId]: normalizedSelectedOptionId,
    }));
  };

  const referenceHtml =
    referenceQuery.data?.additional_info ||
    referenceQuery.data?.ref_message ||
    referenceQuery.data?.ref_message_en ||
    referenceQuery.data?.ref_message_hi ||
    referenceQuery.data?.description ||
    currentQuestion?.rightExplanation ||
    currentQuestion?.refMessage ||
    "";
  const notesItems = notesQuery.data?.items || [];

  const persistTime = async (questionId) => {
    const normalizedQuestionId = Number(questionId || 0);
    if (!normalizedQuestionId) {
      return;
    }

    try {
      const latestQuestionBaseParams = latestQuestionBaseParamsRef.current || {};
      await saveTime.mutateAsync({
        quiz_id: Number(latestQuestionBaseParams.quiz_id || quizId || 0),
        assesment_batch_id: Number(latestQuestionBaseParams.assesment_batch_id || assesmentBatchId || 0),
        assessment_batch_id: Number(latestQuestionBaseParams.assesment_batch_id || assesmentBatchId || 0),
        question_id: normalizedQuestionId,
        timer_seconds: Number(latestQuestionTimersRef.current?.[normalizedQuestionId] || 0),
      });
    } catch (_error) {
      // Best effort.
    }
  };


  useEffect(() => {
    const persistCurrentQuestionTime = () => {
      const questionId = Number(latestCurrentQuestionIdRef.current || timerQuestionIdRef.current || 0);
      if (questionId) {
        persistTime(questionId);
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        persistCurrentQuestionTime();
      }
    };

    window.addEventListener("pagehide", persistCurrentQuestionTime);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      persistCurrentQuestionTime();
      window.removeEventListener("pagehide", persistCurrentQuestionTime);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  const requestNextQuestionsPage = () => {
    const nextPage = Number(activeQuestionsData?.pagination?.nextPage || (questionsQuery.isError ? currentPage : 0));
    const hasMoreDetails = questionsQuery.isError || totalQuestionCount <= 0 || questionOrder.length < totalQuestionCount;
    if (
      !nextPage ||
      fetchingNextPageRef.current ||
      fetchedPagesRef.current.has(nextPage) ||
      !hasMoreDetails
    ) {
      return false;
    }

    fetchingNextPageRef.current = true;
    setIsFetchingNextPage(true);
    setCurrentPage(nextPage);
    if (nextPage === Number(currentPage) && questionsQuery.isError) questionsQuery.refetch();
    return true;
  };

  const requestQuestionPage = (targetPage) => {
    const normalizedTargetPage = Number(targetPage || 0);
    const totalPages = Number(activeQuestionsData?.pagination?.totalPages || 0);
    if (!normalizedTargetPage) {
      return false;
    }

    if (normalizedTargetPage === Number(currentPage)) {
      if (questionsQuery.isError) {
        fetchingNextPageRef.current = true;
        setIsFetchingNextPage(true);
        questionsQuery.refetch();
      }
      return true;
    }

    if (fetchingNextPageRef.current || fetchedPagesRef.current.has(normalizedTargetPage)) {
      return false;
    }

    if (totalPages > 0 && normalizedTargetPage > totalPages) {
      return false;
    }

    fetchingNextPageRef.current = true;
    setIsFetchingNextPage(true);
    setCurrentPage(normalizedTargetPage);
    return true;
  };

  useEffect(() => {
    const fetchedPage = Number(activeQuestionsData?.pagination?.currentPage || 0);
    if (questionsQuery.isPlaceholderData || (fetchedPage && fetchedPage !== Number(currentPage))) {
      return;
    }

    fetchingNextPageRef.current = false;
    setIsFetchingNextPage(false);
  }, [activeQuestionsData, currentPage, questionsQuery.isPlaceholderData]);

  useEffect(() => {
    if (!questionsQuery.isError || questionsQuery.isFetching) return;
    fetchingNextPageRef.current = false;
    pendingNextQuestionIndexRef.current = null;
    pendingQuestionSequenceRef.current = 0;
    setIsFetchingNextPage(false);
    setIsQuestionTransitioning(false);
    setScreenError(questionsQuery.error?.message || "Unable to load the question. Please try again.");
  }, [questionsQuery.isError, questionsQuery.isFetching, questionsQuery.error]);

  const ensureQuestionSequenceLoaded = (targetSequence) => {
    const normalizedTargetSequence = Number(targetSequence || 0);
    if (!normalizedTargetSequence) {
      return false;
    }

    const isAlreadyLoaded = Object.values(questionsById).some(
      (question) => getQuestionSequence(question) === normalizedTargetSequence
    );
    if (isAlreadyLoaded) {
      return true;
    }

    const perPage = Number(activeQuestionsData?.pagination?.perPage || 0);
    const fallbackPerPage = questionOrder.length > 0 ? questionOrder.length : 0;
    const resolvedPerPage = perPage || fallbackPerPage;
    const targetPage =
      resolvedPerPage > 0 ? Math.max(1, Math.ceil(normalizedTargetSequence / resolvedPerPage)) : 0;
    const requested =
      targetPage > 0 ? requestQuestionPage(targetPage) : requestNextQuestionsPage();
    if (requested) {
      pendingQuestionSequenceRef.current = normalizedTargetSequence;
      setIsQuestionTransitioning(true);
    }
    return false;
  };

  const handlePauseQuiz = async () => {
    if (!currentQuestion?.id || togglePauseQuiz.isPending) {
      return;
    }

    const nextPaused = !isQuizPaused;
    const currentSeconds = Number(questionTimers[currentQuestion.id] || 0);

    try {
      await persistTime(currentQuestion.id);
      await togglePauseQuiz.mutateAsync({
        states_id: statesId,
        category_id: categoryId,
        quiz_id: quizId,
        assesment_batch_id: assesmentBatchId,
        question_id: Number(currentQuestion.id),
        selected_lang: selectedLang,
        duration_sec: currentSeconds,
        is_paused: nextPaused ? 1 : 0,
      });
      setIsQuizPaused(nextPaused);
      setIsPauseModalOpen(false);

      if (nextPaused) {
        allowExitRef.current = true;
        if (pendingNavigationRef.current) {
          const nextRoute = pendingNavigationRef.current;
          pendingNavigationRef.current = null;
          navigate(nextRoute);
          return;
        }

        persistQuizFlowState(startQuizState);
        navigateQuizBack(navigate, detailFlowState.quizBackTarget);
      }
    } catch (error) {
      setScreenError(error?.message || "Unable to update pause status.");
      await Swal.fire({
        icon: "error",
        title: "Unable to pause quiz",
        text: error?.message || "Please try again.",
      });
    }
  };

  const handleSaveCurrentAnswer = async (questionIdArg, selectedOptionIdArg) => {
    const resolvedQuestionId = Number(questionIdArg || currentQuestion?.id || 0);
    const resolvedQuestion = questionsById[resolvedQuestionId] || null;
    const resolvedIsGuess = Boolean(guessFlagsRef.current[resolvedQuestionId]);
    const resolvedOptionId = resolveSelectedOptionId(
      resolvedQuestion,
      selectedOptionIdArg || selectedAnswers[resolvedQuestionId] || 0
    );

    if (!resolvedQuestionId || !resolvedOptionId) {
      return true;
    }

    if (resolvedQuestion?.isAnswered && !isEvaluationMode) {
      return true;
    }

    try {
      setScreenError("");
      await saveAnswer.mutateAsync({
        ...questionBaseParams,
        question_id: resolvedQuestionId,
        user_answer_id: resolvedOptionId,
        timer_seconds: Number(questionTimers[resolvedQuestionId] || 0),
        is_guess: resolvedIsGuess ? 1 : 0,
      });
      syncSelectedAnswerState(resolvedQuestionId, resolvedOptionId);
      syncQuestionPatch(resolvedQuestionId, {
        selectedOptionId: resolvedOptionId,
        isAnswered: true,
        isGuess: resolvedIsGuess,
        options: syncOptionSelection(resolvedQuestion?.options || [], resolvedOptionId),
        raw: {
          ...(resolvedQuestion?.raw || {}),
          selected_option_id: resolvedOptionId,
          is_question_answered: 1,
          is_answered: 1,
        },
      });
      paletteSummaryQuery.refetch();
      setIsExplanationOpen(true);
      return true;
    } catch (error) {
      const message = error?.message || "Unable to save answer.";
      setScreenError(message);
      await Swal.fire({
        icon: "error",
        title: "Unable to save answer",
        text: message,
      });
      return false;
    }
  };

  const handleSelectOption = async (optionId) => {
    if (
      !currentQuestion?.id ||
      isQuizPaused ||
      (currentQuestion?.isAnswered && !isEvaluationMode)
    ) {
      return;
    }

    syncSelectedAnswerState(currentQuestion.id, optionId);
    syncQuestionPatch(currentQuestion.id, {
      selectedOptionId: Number(optionId),
      options: syncOptionSelection(currentQuestion?.options || [], optionId),
      raw: {
        ...(currentQuestion?.raw || {}),
        selected_option_id: Number(optionId),
      },
    });

    await handleSaveCurrentAnswer(currentQuestion.id, optionId);
  };

  const handleToggleGuess = async (nextValue) => {
    if (!currentQuestion?.id || isQuizPaused) {
      return;
    }

    setGuessFlags((current) => ({
      ...current,
      [currentQuestion.id]: nextValue,
    }));
    guessFlagsRef.current = {
      ...guessFlagsRef.current,
      [currentQuestion.id]: nextValue,
    };
    syncQuestionPatch(currentQuestion.id, { isGuess: nextValue });

    if (!currentQuestion?.isAnswered) {
      return;
    }

    try {
      await saveGuess.mutateAsync({
        quiz_id: quizId,
        assesment_batch_id: assesmentBatchId,
        question_id: Number(currentQuestion.id),
        is_guess: nextValue ? 1 : 0,
      });
      paletteSummaryQuery.refetch();
    } catch (error) {
      setGuessFlags((current) => ({
        ...current,
        [currentQuestion.id]: !nextValue,
      }));
      guessFlagsRef.current = {
        ...guessFlagsRef.current,
        [currentQuestion.id]: !nextValue,
      };
      syncQuestionPatch(currentQuestion.id, { isGuess: !nextValue });
      setScreenError(error?.message || "Unable to update guess answer.");
    }
  };

  const goToQuestion = async (target) => {
    if (isQuizPaused || isQuestionLoading) {
      return;
    }

    const requestedQuestionId = Number(target?.id || target?.question_id || target || 0);
    const requestedSequence = getQuestionSequence(target);
    const normalizedQuestionId =
      requestedQuestionId ||
      Number(
        Object.values(questionsById).find(
          (question) =>
            Number(question?.serialNo || question?.question_index || 0) === requestedSequence
        )?.id || 0
      );
    if (!normalizedQuestionId || normalizedQuestionId === Number(currentQuestionId)) {
      if (normalizedQuestionId) {
        return;
      }
      if (!requestedSequence) {
        return;
      }
      const loaded = ensureQuestionSequenceLoaded(requestedSequence);
      if (!loaded) {
        return;
      }
    }

    const targetQuestion = questionsById[normalizedQuestionId] || null;
    if (!targetQuestion) {
      if (requestedSequence) ensureQuestionSequenceLoaded(requestedSequence);
      return;
    }
    const targetSelectedOptionId = resolveSelectedOptionId(
      targetQuestion,
      selectedAnswers[normalizedQuestionId] || 0
    );
    if (targetQuestion?.isAnswered && targetSelectedOptionId) {
      syncQuestionPatch(normalizedQuestionId, {
        selectedOptionId: targetSelectedOptionId,
        options: syncOptionSelection(targetQuestion?.options || [], targetSelectedOptionId),
      });
    }

    setIsQuestionTransitioning(true);
    await persistTime(currentQuestionId);
    setCurrentQuestionId(normalizedQuestionId);
    setIsQuestionTransitioning(false);
    if (isPaletteDrawerEnabled) {
      setIsPaletteDrawerOpen(false);
    }
  };

  const handleGoPrevious = async () => {
    if (currentQuestionIndex <= 0 || isQuizPaused || isQuestionLoading) {
      return;
    }

    const targetQuestionId = questionOrder[currentQuestionIndex - 1];
    const targetQuestion = questionsById[targetQuestionId] || null;
    const targetSelectedOptionId = resolveSelectedOptionId(
      targetQuestion,
      selectedAnswers[targetQuestionId] || 0
    );
    if (targetQuestion?.isAnswered && targetSelectedOptionId) {
      syncQuestionPatch(targetQuestionId, {
        selectedOptionId: targetSelectedOptionId,
        options: syncOptionSelection(targetQuestion?.options || [], targetSelectedOptionId),
      });
    }

    setIsQuestionTransitioning(true);
    await persistTime(currentQuestionId);
    setCurrentQuestionId(targetQuestionId);
    setIsQuestionTransitioning(false);
  };

  const handleGoNext = async () => {
    if (isQuizPaused || isQuestionLoading) {
      return;
    }

    if (shouldShowSubmit) {
      setIsSubmitModalOpen(true);
      return;
    }

    const isSaved = await handleSaveCurrentAnswer();
    if (!isSaved) {
      return;
    }

    await persistTime(currentQuestionId);

    if (currentQuestionIndex >= questionOrder.length - 2) {
      requestNextQuestionsPage();
    }

    if (currentQuestionIndex >= 0 && currentQuestionIndex < questionOrder.length - 1) {
      const targetQuestionId = questionOrder[currentQuestionIndex + 1];
      const targetQuestion = questionsById[targetQuestionId] || null;
      const targetSelectedOptionId = resolveSelectedOptionId(
        targetQuestion,
        selectedAnswers[targetQuestionId] || 0
      );
      if (targetQuestion?.isAnswered && targetSelectedOptionId) {
        syncQuestionPatch(targetQuestionId, {
          selectedOptionId: targetSelectedOptionId,
          options: syncOptionSelection(targetQuestion?.options || [], targetSelectedOptionId),
        });
      }

      setCurrentQuestionId(targetQuestionId);
      if (currentQuestionIndex + 1 >= questionOrder.length - 1) {
        requestNextQuestionsPage();
      }
      return;
    }

    const requested = requestNextQuestionsPage();
    if (requested || fetchingNextPageRef.current) {
      pendingNextQuestionIndexRef.current = currentQuestionIndex + 1;
      setIsQuestionTransitioning(true);
    }
  };

  const handleSubmitQuiz = async ({ autosubmit = 0 } = {}) => {
    const isSaved = await handleSaveCurrentAnswer();
    if (!isSaved) {
      return;
    }

    await persistTime(currentQuestionId);

    try {
      setScreenError("");
      await saveAssessmentSubmitStatus.mutateAsync({
        quiz_id: quizId,
        assesment_batch_id: assesmentBatchId,
        submit_status: 1,
        autosubmit: Number(autosubmit || 0),
      });
      const response = await submitQuiz.mutateAsync(questionBaseParams);
      const nextFlowState = buildQuizFlowState({
        ...detailFlowState,
        quiz_submission: response?.payload || {},
        quizBackTarget: buildQuizBackTarget(QUIZ_ROUTES.start, startQuizState),
      });

      persistQuizFlowState(nextFlowState);
      navigate(QUIZ_ROUTES.answerSheet, {
        state: nextFlowState,
        replace: true,
      });
    } catch (error) {
      const message = error?.message || "Unable to submit quiz right now.";
      setScreenError(message);
      await Swal.fire({
        icon: "error",
        title: "Unable to submit quiz",
        text: message,
      });
    }
  };

  useEffect(() => {
    if (
      remainingSeconds !== 0 ||
      autoSubmitTriggeredRef.current ||
      submitQuiz.isPending ||
      saveAssessmentSubmitStatus.isPending
    ) {
      return;
    }

    autoSubmitTriggeredRef.current = true;
    setIsSubmitModalOpen(true);
    handleSubmitQuiz({ autosubmit: 1 });
  }, [remainingSeconds, submitQuiz.isPending, saveAssessmentSubmitStatus.isPending]);

  const handleGoBack = async () => {
    setIsPauseModalOpen(true);
  };

  const handleClosePauseModal = () => {
    if (togglePauseQuiz.isPending) {
      return;
    }

    pendingNavigationRef.current = null;
    setIsPauseModalOpen(false);
  };

  const handleToggleBookmark = async () => {
    if (!currentQuestion?.id || isQuizPaused || bookmarkMutation.isPending) {
      return;
    }

    const previousValue = Boolean(currentQuestion.isBookmarked);
    syncQuestionPatch(currentQuestion.id, {
      isBookmarked: !previousValue,
    });

    try {
      const result = await bookmarkMutation.mutateAsync({
        question_id: Number(currentQuestion.id),
      });

      syncQuestionPatch(currentQuestion.id, {
        isBookmarked: Number(result?.payload?.bookmark_status ?? 0) === 1,
      });
    } catch (error) {
      syncQuestionPatch(currentQuestion.id, {
        isBookmarked: previousValue,
      });
      setScreenError(error?.message || "Unable to update bookmark.");
    }
  };

  const handleToggleLike = async () => {
    if (!currentQuestion?.id || isQuizPaused || likeMutation.isPending) {
      return;
    }

    const previousLiked = Boolean(currentQuestion.isLiked);
    const previousCount = Number(currentQuestion.likeCount || 0);
    syncQuestionPatch(currentQuestion.id, {
      isLiked: !previousLiked,
      likeCount: Math.max(0, previousCount + (previousLiked ? -1 : 1)),
    });

    try {
      const result = await likeMutation.mutateAsync({
        question_id: Number(currentQuestion.id),
      });

      syncQuestionPatch(currentQuestion.id, {
        isLiked: Number(result?.payload?.like_status ?? 0) === 1,
        likeCount: Number(result?.payload?.total_liked ?? previousCount),
      });
    } catch (error) {
      syncQuestionPatch(currentQuestion.id, {
        isLiked: previousLiked,
        likeCount: previousCount,
      });
      setScreenError(error?.message || "Unable to update like.");
    }
  };

  const handleDeleteNote = async (note) => {
    const noteId = Number(note?.id || 0);
    const questionId = Number(currentQuestion?.id || 0);

    if (!noteId || !questionId || deleteNote.isPending) {
      return;
    }

    const result = await Swal.fire({
      icon: "warning",
      title: "Delete Sticky Note?",
      text: "This note will be removed from your account for this question.",
      showCancelButton: true,
      confirmButtonText: "Delete",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#e53935",
    });

    if (!result.isConfirmed) {
      return;
    }

    try {
      await deleteNote.mutateAsync({
        note_id: noteId,
        question_id: questionId,
      });

      syncQuestionPatch(questionId, {
        notesCount: Math.max(0, Number(currentQuestion?.notesCount || notesItems.length || 0) - 1),
      });
    } catch (error) {
      await Swal.fire({
        icon: "error",
        title: "Unable to Delete Note",
        text: error?.response?.data?.message || error?.message || "Please try again.",
      });
    }
  };

  const paletteItems = useMemo(
    () => {
      const sourceItems = paletteApiItems.length
        ? paletteApiItems
        : questionOrder.map((questionId) => questionsById[questionId]).filter(Boolean);

      return sourceItems.map((item, index) => {
        const questionId = Number(item?.question_id || item?.id || 0);
        const sequence = Number(
          item?.serialNo || item?.question_index || item?.question_number || item?.count || index + 1
        );
        const loadedQuestion =
          questionsById[questionId] ||
          Object.values(questionsById).find(
            (question) => Number(question?.serialNo || question?.question_index || 0) === sequence
          ) ||
          null;
        const mergedQuestion = loadedQuestion
          ? mergeQuestion(item, loadedQuestion)
          : item;
        const mergedQuestionId = Number(
          mergedQuestion?.question_id || mergedQuestion?.id || questionId || 0
        );
        const selectedOptionId = resolveSelectedOptionId(
          mergedQuestion,
          selectedAnswers[mergedQuestionId] || 0
        );
        const selectedOption = (mergedQuestion?.options || []).find(
          (option) => Number(option.id) === Number(selectedOptionId)
        );
        const isCurrent =
          Number(currentQuestion?.serialNo || currentQuestion?.question_index || 0) === sequence;
        const isAnswered =
          Boolean(mergedQuestion?.isAnswered) ||
          isAffirmativeFlag(mergedQuestion?.raw?.is_question_answered) ||
          isAffirmativeFlag(mergedQuestion?.raw?.is_answered) ||
          isAffirmativeFlag(mergedQuestion?.raw?.question_answered) ||
          isAffirmativeFlag(mergedQuestion?.raw?.attempted);
        let status = "unsolved";

        if (isAnswered) {
          if (isEvaluationMode) {
            status = "solved";
          } else {
          const correctnessFlag = mergedQuestion?.raw?.is_answer_correct;
          if (
            correctnessFlag !== null &&
            correctnessFlag !== undefined &&
            correctnessFlag !== ""
          ) {
            status = isAffirmativeFlag(correctnessFlag) ? "correct" : "incorrect";
          } else if (selectedOption?.isCorrect) {
            status = "correct";
          } else if (selectedOptionId) {
            status = "incorrect";
          } else {
            status = "solved";
          }
          }
        }

        return {
          id: mergedQuestionId || sequence,
          question_id: mergedQuestionId,
          serialNo: sequence,
          question: mergedQuestion?.question || item?.question || "",
          questionHtml: mergedQuestion?.questionHtml || item?.questionHtml || "",
          question_index: sequence,
          status,
          isCurrent,
        };
      });
    },
    [
      currentQuestion?.question_index,
      currentQuestion?.serialNo,
      isEvaluationMode,
      paletteApiItems,
      questionOrder,
      questionsById,
      selectedAnswers,
    ]
  );

  const derivedPaletteSummary = useMemo(() => {
    const solvedCount = paletteItems.filter(
      (item) => item.status === "solved" || item.status === "correct" || item.status === "incorrect"
    ).length;
    const correctCount = paletteItems.filter((item) => item.status === "correct").length;
    const incorrectCount = paletteItems.filter((item) => item.status === "incorrect").length;
    const totalQuestions =
      Number(paletteSummaryQuery.data?.totalQuestions || 0) ||
      totalQuestionCount ||
      paletteItems.length;
    const unsolvedCount = Math.max(0, totalQuestions - solvedCount);

    return {
      ...(paletteSummaryQuery.data || {}),
      totalQuestions,
      solvedCount: Math.max(Number(paletteSummaryQuery.data?.solvedCount || 0), solvedCount),
      unsolvedCount:
        paletteSummaryQuery.data?.unsolvedCount === undefined
          ? unsolvedCount
          : Math.min(Number(paletteSummaryQuery.data?.unsolvedCount || 0), unsolvedCount),
      correctCount: Math.max(Number(paletteSummaryQuery.data?.correctCount || 0), correctCount),
      incorrectCount: Math.max(
        Number(paletteSummaryQuery.data?.incorrectCount || 0),
        incorrectCount
      ),
    };
  }, [paletteItems, paletteSummaryQuery.data, totalQuestionCount]);

  const visiblePaletteItems = useMemo(
    () => paletteItems.slice(0, sidebarVisibleCount),
    [paletteItems, sidebarVisibleCount]
  );
  const syncedKnownCount = Math.max(totalQuestionCount, paletteItems.length, questionOrder.length);
  const canShowMorePalette =
    sidebarVisibleCount < syncedKnownCount ||
    (Number(activeQuestionsData?.pagination?.nextPage || 0) > 0 &&
      (totalQuestionCount <= 0 || questionOrder.length < totalQuestionCount));
  const currentQuestionLabel = `${currentQuestion?.serialNo || 0} of ${totalQuestionCount || 0}`;
  const shouldShowSubmit =
    Boolean(currentQuestion?.isLast) &&
    Number(activeQuestionsData?.pagination?.nextPage || 0) === 0;
  const footerStats = [
    { key: "like", icon: currentQuestion?.isLiked ? "bi-heart-fill" : "bi-heart", label: "Like", value: String(currentQuestion?.likeCount || 0) },
    { key: "view", icon: "bi-eye", label: "View", value: String(currentQuestion?.viewCount || 0), modal: "view" },
    { key: "report", icon: "bi-exclamation-circle", label: "Report", value: String(currentQuestion?.reportCount || 0), modal: "report" },
    {
      key: "notes",
      icon: "bi-journal-text",
      label: "Notes",
      value: String(notesQuery.data?.totalNotes ?? currentQuestion?.notesCount ?? 0),
      modal: "notes",
    },
  ];
  const submitStats = useMemo(() => {
    const solvedCount = paletteItems.filter(
      (item) => item.status === "solved" || item.status === "correct" || item.status === "incorrect"
    ).length;
    const correctCount = paletteItems.filter((item) => item.status === "correct").length;
    const incorrectCount = paletteItems.filter((item) => item.status === "incorrect").length;
    const unsolvedCount = Math.max(0, totalQuestionCount - solvedCount);

    return {
      total_questions: totalQuestionCount,
      guess_score: 0,
      total_answered: solvedCount,
      not_answered: unsolvedCount,
      unvisited_questions: Math.max(0, totalQuestionCount - paletteItems.length),
      skipped_questions: unsolvedCount,
      correct_questions: correctCount,
      incorrect_questions: incorrectCount,
    };
  }, [paletteItems, totalQuestionCount]);
  const totalElapsedSeconds = useMemo(
    () =>
      Object.values(questionTimers).reduce(
        (sum, seconds) => sum + Number(seconds || 0),
        0
      ),
    [questionTimers]
  );
  const submitTimeLeft = useMemo(() => {
    if (remainingSeconds !== null) {
      return formatDuration(remainingSeconds);
    }

    const totalDurationSeconds = Number(
      activeQuestionsData?.duration ||
        activeQuestionsData?.durations ||
        currentQuestion?.raw?.durations ||
        0
    );

    if (totalDurationSeconds > 0) {
      return formatDuration(Math.max(0, totalDurationSeconds - totalElapsedSeconds));
    }

    return formatDuration(totalElapsedSeconds);
  }, [
    activeQuestionsData?.duration,
    activeQuestionsData?.durations,
    currentQuestion?.raw?.durations,
    remainingSeconds,
    totalElapsedSeconds,
  ]);

  const renderFooterNav = (variant) => (
    <div className={`footer-nav-fixed ${variant}`}>
      <div className="qbank-navRow" aria-label="Question navigation">
        <button
          type="button"
          className="qbank-navBtn qbank-navBtn--prev"
          onClick={handleGoPrevious}
          disabled={currentQuestionIndex <= 0 || isQuizPaused || isQuestionLoading}
        >
          <i className="bi bi-arrow-left" aria-hidden="true" />
          <span>Previous</span>
        </button>

        <button
          type="button"
          className="qbank-navMenu"
          aria-label="Open question list"
          aria-expanded={isPaletteDrawerOpen}
          onClick={() => {
            if (isPaletteDrawerEnabled) {
              setIsPaletteDrawerOpen(true);
            }
          }}
          disabled={isQuizPaused}
        >
          <i className="bi bi-list" aria-hidden="true" />
        </button>

        <button
          type="button"
          className="qbank-navBtn qbank-navBtn--next"
          onClick={handleGoNext}
          disabled={
            isQuizPaused || isQuestionLoading ||
            saveAnswer.isPending ||
            togglePauseQuiz.isPending ||
            submitQuiz.isPending ||
            saveAssessmentSubmitStatus.isPending
          }
        >
          <span>
            {submitQuiz.isPending || saveAssessmentSubmitStatus.isPending
              ? "Submitting..."
              : saveAnswer.isPending
                ? "Saving..."
                : shouldShowSubmit
                  ? "Submit"
                  : "Next"}
          </span>
          <i className="bi bi-arrow-right" aria-hidden="true" />
        </button>
      </div>

      <div className="qbank-stats">
        {footerStats.map((stat) => (
          <button
            key={stat.key}
            type="button"
            className={`qbank-stat${stat.key === "like" && currentQuestion?.isLiked ? " qbank-stat--active" : ""}`}
            onClick={() => {
              if (stat.key === "like") {
                handleToggleLike();
                return;
              }

              if (stat.modal) {
                setActiveModal(stat.modal);
              }
            }}
          >
            <i className={`bi ${stat.icon}`} />
            <span>{stat.label}</span>
            {stat.value ? <strong>{stat.value}</strong> : null}
          </button>
        ))}
      </div>
    </div>
  );

  if (!quizId || !categoryId || !assesmentBatchId) {
    return <div className="qbp-status">Quiz session details are missing.</div>;
  }

  if ((questionsQuery.isLoading || questionsQuery.isFetching) && !questionOrder.length) {
    return (
      <HomeDashboardLayout showHeader={false}>
        <QuizDetailShimmer />
      </HomeDashboardLayout>
    );
  }

  if (questionsQuery.isError && !questionOrder.length) {
    return (
      <div className="qbp-status">
        {questionsQuery.error?.message || "Unable to load quiz questions."}
      </div>
    );
  }

  return (
    <>
      <style>{`
        .homeDashboard-wrapper .main-content {
          height: 100vh;
          overflow: hidden;
          box-sizing: border-box;
          padding-bottom: 35px;
        }

        .homeDashboard-wrapper .page-content {
          height: 100%;
          overflow: hidden;
          padding-right: 0;
        }

        .quiz-detail-headerTitle {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 16px;
          flex-wrap: wrap;
        }

        .quiz-detail-timerPill {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          padding: 6px 8px 6px 14px;
          border: 1px solid #d8d9ff;
          border-radius: 999px;
          background: linear-gradient(135deg, #f7f4ff 0%, #eef2ff 100%);
          box-shadow: 0 10px 24px rgba(83, 72, 255, 0.08);
        }

        .quiz-detail-timerPill__time {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          color: #2c226b;
          font-size: 14px;
          font-weight: 700;
          line-height: 1;
          white-space: nowrap;
        }

        .quiz-detail-timerPill__pause {
          width: 34px;
          height: 34px;
          border: 0;
          border-radius: 999px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          background: linear-gradient(135deg, #6d3df5 0%, #4a31f4 100%);
          color: #fff;
          box-shadow: 0 8px 18px rgba(74, 49, 244, 0.28);
        }

        .quiz-detail-timerPill__pause:disabled {
          opacity: 0.7;
          cursor: not-allowed;
          box-shadow: none;
        }
      `}</style>
      <HomeDashboardLayout showHeader={false}>
        <div className="categories-page qbank-detail-page" style={{ background: "#fff" }}>
          <div className="categories-page-header">
            <div>
              <button type="button" className="categories-back-button" onClick={handleGoBack}>
                <i className="bi bi-chevron-left" />
              </button>
            </div>
            <div>
              <div className="quiz-detail-headerTitle">
                {/* <h1 className="categories-page-title">Questions</h1> */}
                {remainingSeconds !== null ? (
                  <div className="quiz-detail-timerPill" aria-label="Global quiz timer">
                    <span className="quiz-detail-timerPill__time">
                      <i className="bi bi-hourglass-split" aria-hidden="true" />
                      <span>{formatDuration(remainingSeconds)}</span>
                    </span>
                    {Boolean(activeQuestionsData?.pauseEnabled) ? (
                      <button
                        type="button"
                        className="quiz-detail-timerPill__pause"
                        aria-label={isQuizPaused ? "Resume quiz" : "Pause quiz"}
                        onClick={() => {
                          if (isQuizPaused) {
                            handlePauseQuiz();
                            return;
                          }

                          setIsPauseModalOpen(true);
                        }}
                        disabled={togglePauseQuiz.isPending}
                      >
                        <i
                          className={`bi ${isQuizPaused ? "bi-play-fill" : "bi-pause-fill"}`}
                          aria-hidden="true"
                        />
                      </button>
                    ) : null}
                  </div>
                ) : null}
              </div>
            </div>
            <div>
              <HeaderProfileSection />
            </div>
          </div>

          <div className="qb-Detail-content">
            <div className="d-flex qbank-detail-main">
              <section className="qbank-LeftContent" aria-label="Question and answers">
                <div className="qbank-card qbank-card--question">
                  <div className="qbank-questionTop">
                    <span className="qbank-progress">{currentQuestionLabel}</span>

                    <div className="qbank-timer" aria-label="Question timer">
                      <i className="bi bi-clock" aria-hidden="true" />
                      <span>{formatDuration(questionTimers[currentQuestion?.id] || 0)}</span>
                    </div>

                    <div className="qbank-toolbar" aria-label="Question tools">
                      <button
                        type="button"
                        className="qbank-toolbarBtn"
                        aria-label="Bookmark"
                        onClick={handleToggleBookmark}
                      >
                        <i
                          className={`bi ${
                            currentQuestion?.isBookmarked ? "bi-bookmark-fill" : "bi-bookmark"
                          }`}
                          aria-hidden="true"
                        />
                      </button>
                      <button
                        type="button"
                        className="qbank-toolbarBtn"
                        aria-label="Question activity"
                        onClick={() => setActiveModal("view")}
                      >
                        <i className="bi bi-info-circle" aria-hidden="true" />
                      </button>
                      <button
                        type="button"
                        className="qbank-toolbarBtn"
                        aria-label="Like"
                        onClick={handleToggleLike}
                      >
                        <i
                          className={`bi ${currentQuestion?.isLiked ? "bi-heart-fill" : "bi-heart"}`}
                        />
                      </button>
                    </div>
                  </div>

                  <div className="data-question-scroll" aria-busy={isQuestionLoading}>
                    {screenError ? <p className="text-danger">{screenError}</p> : null}

                    {isQuestionLoading ? <QuizQuestionShimmer /> : <>
                    <div className="qbank-questionBlock">
                      <div
                        className="qbank-question"
                        dangerouslySetInnerHTML={{
                          __html: DOMPurify.sanitize(
                            currentQuestion?.questionHtml ||
                              currentQuestion?.question ||
                              "Question not available."
                          ),
                        }}
                      />
                      {currentQuestion?.questionStatementOne || currentQuestion?.questionStatementTwo ? (
                        <p className="qbank-questionSub">
                          {stripHtml(
                            `${currentQuestion?.questionStatementOne || ""} ${
                              currentQuestion?.questionStatementTwo || ""
                            }`
                          )}
                        </p>
                      ) : null}
                    </div>

                    {currentQuestion?.questionImage ? (
                      <div className="mb-3">
                        <img
                          src={currentQuestion.questionImage}
                          alt="Question"
                          className="img-fluid rounded"
                        />
                      </div>
                    ) : null}

                    <div className="qbank-options" role="list" aria-label="Answer options">
                      {(currentQuestion?.options || []).map((option, index) => (
                        <button
                          key={option.id}
                          type="button"
                          className={`qbank-option qbank-option--${toOptionTone(
                            currentQuestion,
                            option,
                            option.id,
                            selectedAnswers[currentQuestion?.id] || 0,
                            !isEvaluationMode
                          )}`}
                          aria-label={`Option ${toOptionLabel(index)}`}
                          onClick={() => handleSelectOption(option.id)}
                          disabled={Boolean(currentQuestion?.isAnswered && !isEvaluationMode) || isQuizPaused}
                        >
                          <span className="qbank-option__badge">{toOptionLabel(index)}</span>
                          <span className="qbank-option__copy">
                            <span
                              className="qbank-option__english"
                              dangerouslySetInnerHTML={{
                                __html: DOMPurify.sanitize(option.label || ""),
                              }}
                            />
                          </span>
                        </button>
                      ))}
                    </div>

                    <label className="qbank-guess" htmlFor="guess-answer">
                      <span className="qbank-guess__check">
                        <input
                          id="guess-answer"
                          type="checkbox"
                          checked={Boolean(guessFlags[currentQuestion?.id])}
                          onChange={(event) => handleToggleGuess(event.target.checked)}
                          disabled={isQuizPaused || saveGuess.isPending}
                        />
                        <span className="qbank-guess__box" aria-hidden="true" />
                      </span>
                      <span className="qbank-guess__label">Guess Answer</span>
                      <div className="info-btn">
                        <i className="bi bi-info-circle-fill" />
                      </div>
                    </label>
                    </>}
                  </div>

                  {renderFooterNav("footer-nav-fixed--desktop")}
                </div>
              </section>

              <aside className="qbank-rightContent" aria-label="Explanation and notes">
                <section className="qbank-sideCard">
                  <div className="qbank-sideCard__head">
                    <h3>PYP</h3>
                    <button
                      type="button"
                      className="qbank-sideCard__toggle"
                      onClick={() => setIsPYPOpen((current) => !current)}
                    >
                      <i className={`bi ${isPYPOpen ? "bi-chevron-up" : "bi-chevron-down"}`} />
                    </button>
                  </div>

                  {isPYPOpen ? (
                    <div className="qbank-sideCard__body">
                      <div className="qbank-sideCard__subtitle">
                        {(currentQuestion?.pyqMappings || []).length ? (
                          currentQuestion.pyqMappings.map((item, index) => (
                            <span key={`${item?.master_exam || "pyq"}-${index}`}>
                              {item?.master_exam || item?.exam_category || "Previous Year"}{" "}
                              {item?.year || ""}
                            </span>
                          ))
                        ) : (
                          <span>No PYP data available.</span>
                        )}
                      </div>
                    </div>
                  ) : null}
                </section>

                {!isEvaluationMode ? (
                  <section className="qbank-sideCard">
                    <div className="qbank-sideCard__head">
                      <h3>Explanation</h3>
                      <button
                        type="button"
                        className="qbank-sideCard__toggle"
                        onClick={() => setIsExplanationOpen((current) => !current)}
                      >
                        <i className={`bi ${isExplanationOpen ? "bi-chevron-up" : "bi-chevron-down"}`} />
                      </button>
                    </div>

                    {isExplanationOpen ? (
                      <div className="qbank-sideCard__body">
                        <div className="qbank-sideCard__subtitle">
                          {!currentQuestion?.isAnswered ? (
                            <span>Answer the question to view the explanation.</span>
                          ) : referenceQuery.isLoading ? (
                            <span>Loading explanation...</span>
                          ) : referenceHtml ? (
                            <div
                              dangerouslySetInnerHTML={{
                                __html: DOMPurify.sanitize(referenceHtml),
                              }}
                            />
                          ) : (
                            <span>No explanation available.</span>
                          )}
                        </div>
                      </div>
                    ) : null}
                  </section>
                ) : null}

                <section className="qbank-sideCard qbank-sideCard--notes">
                  <div className="qbank-sideCard__head">
                    <h3>Sticky Notes</h3>
                    <button
                      type="button"
                      className="qbank-sideCard__toggle"
                      onClick={() => setIsNotesOpen((current) => !current)}
                    >
                      <i className={`bi ${isNotesOpen ? "bi-chevron-up" : "bi-chevron-down"}`} />
                    </button>
                  </div>

                  {isNotesOpen ? (
                    <div className="qbank-notesList">
                      {notesQuery.isLoading ? (
                        <div className="qbank-note">
                          <p className="qbank-note__text">Loading notes...</p>
                        </div>
                      ) : notesItems.length ? (
                        notesItems.map((note, index) => (
                          <div className="qbank-note" key={note.id || index}>
                            <span className="qbank-note__index">{index + 1}.</span>
                            <p className="qbank-note__text">{note.description}</p>
                            <button
                              type="button"
                              className="qbank-note__close"
                              aria-label="Hide notes"
                              onClick={() => handleDeleteNote(note)}
                              disabled={deleteNote.isPending}
                            >
                              <i className="bi bi-x" />
                            </button>
                          </div>
                        ))
                      ) : (
                        <div className="qbank-note">
                          <p className="qbank-note__text">No sticky notes available.</p>
                        </div>
                      )}
                    </div>
                  ) : null}
                </section>
              </aside>
            </div>

            {renderFooterNav("footer-nav-fixed--mobile")}

            <div className="Qbank-sidebar qbank-detail-sidebar qbank-detail-sidebar--desktop">
              <QuestionPallete
                items={visiblePaletteItems}
                summaryCounts={derivedPaletteSummary}
                showCorrectness={!isEvaluationMode}
                activeQuestionId={currentQuestionId}
                activeQuestionNumber={currentQuestion?.serialNo || currentQuestion?.question_index || 0}
                onSelect={goToQuestion}
                canShowMore={canShowMorePalette}
                onShowMore={() => {
                  const nextVisibleCount = sidebarVisibleCount + 50;
                  setSidebarVisibleCount(nextVisibleCount);
                  if (nextVisibleCount > questionOrder.length && questionOrder.length < syncedKnownCount) {
                    requestNextQuestionsPage();
                  }
                }}
                isLoadingMore={isFetchingNextPage}
              />
            </div>
          </div>

          {isPaletteDrawerEnabled ? (
            <>
              <button
                type="button"
                className={`qbank-drawer-backdrop ${isPaletteDrawerOpen ? "is-open" : ""}`}
                aria-label="Close question palette"
                onClick={() => setIsPaletteDrawerOpen(false)}
              />

              <aside
                className={`Qbank-sidebar qbank-detail-sidebar qbank-detail-sidebar--drawer ${
                  isPaletteDrawerOpen ? "is-open" : ""
                }`}
                aria-hidden={!isPaletteDrawerOpen}
                aria-label="Question palette drawer"
              >
                <div className="qbank-detail-sidebar__head">
                  <h2>Questions</h2>

                  <button
                    type="button"
                    className="qbank-detail-sidebar__close"
                    aria-label="Close question palette"
                    onClick={() => setIsPaletteDrawerOpen(false)}
                  >
                    <i className="bi bi-x-lg" aria-hidden="true" />
                  </button>
                </div>

                <div className="qbank-detail-sidebar__body">
                  <QuestionPallete
                    items={visiblePaletteItems}
                    summaryCounts={derivedPaletteSummary}
                    showCorrectness={!isEvaluationMode}
                    activeQuestionId={currentQuestionId}
                    activeQuestionNumber={currentQuestion?.serialNo || currentQuestion?.question_index || 0}
                    onSelect={goToQuestion}
                    canShowMore={canShowMorePalette}
                    onShowMore={() => {
                      const nextVisibleCount = sidebarVisibleCount + 50;
                      setSidebarVisibleCount(nextVisibleCount);
                      if (nextVisibleCount > questionOrder.length && questionOrder.length < syncedKnownCount) {
                        requestNextQuestionsPage();
                      }
                    }}
                    isLoadingMore={isFetchingNextPage}
                  />
                </div>
              </aside>
            </>
          ) : null}
        </div>

        <ViewModal open={activeModal === "view"} onClose={() => setActiveModal(null)} question={currentQuestion} />
        <ReportModal
          open={activeModal === "report"}
          onClose={() => setActiveModal(null)}
          question={currentQuestion}
          questionQueryParams={questionBaseParams}
        />
        <StickyNotesPopup
          open={activeModal === "notes"}
          onClose={() => setActiveModal(null)}
          question={currentQuestion}
          questionQueryParams={questionBaseParams}
        />
        <PauseTestQuizModal
          open={isPauseModalOpen}
          onClose={handleClosePauseModal}
          onPause={handlePauseQuiz}
          description="You can resume this quiz later from where you left off."
          isPausing={togglePauseQuiz.isPending}
        />
        <QuizSubmitModal
          open={isSubmitModalOpen}
          onClose={() => {
            if (submitQuiz.isPending || saveAssessmentSubmitStatus.isPending) {
              return;
            }
            setIsSubmitModalOpen(false);
          }}
          onSubmit={handleSubmitQuiz}
          statsData={submitStats}
          timeLeft={submitTimeLeft}
          showAccuracyCounts={!isEvaluationMode}
          isSubmitting={submitQuiz.isPending || saveAssessmentSubmitStatus.isPending}
        />
      </HomeDashboardLayout>
    </>
  );
};

export default QuizDetail;










